import { getLocalBuildInfo } from './buildInfo';

export const BUILD_META_PATH = '/build-meta.json';
export const FRESH_BUILD_QUERY_PARAM = '__fresh_build';
export const FORCED_REFRESH_STORAGE_KEY = 'stale-build:last-forced-refresh';

const BUILD_CHECK_TTL_MS = 1500;
const CHUNK_LOAD_PATTERNS = [
  'failed to fetch dynamically imported module',
  'importing a module script failed',
  'chunkloaderror',
];

let inFlightBuildCheck = null;
let lastBuildCheckAt = 0;
let lastBuildCheckResult = null;

const neverSettlingPromise = () => new Promise(() => {});
const getRuntimeWindow = () => (typeof window !== 'undefined' ? window : undefined);
const getRuntimeFetch = () => {
  const runtimeWindow = getRuntimeWindow();
  return typeof runtimeWindow?.fetch === 'function' ? runtimeWindow.fetch.bind(runtimeWindow) : undefined;
};
const getRuntimeSessionStorage = () => getRuntimeWindow()?.sessionStorage;
const getRuntimeHistory = () => getRuntimeWindow()?.history;
const getRuntimeLocation = () => getRuntimeWindow()?.location;

const hasAssetHint = (value) => String(value || '').toLowerCase().includes('/assets/');

const safeReadJson = (value) => {
  try {
    return value ? JSON.parse(value) : null;
  } catch (error) {
    return null;
  }
};

const buildForcedRefreshMarker = ({ pathname, buildId }) => ({
  pathname: String(pathname || '/'),
  buildId: String(buildId || 'runtime-mismatch'),
});

export function isChunkLoadError(error) {
  const message = String(
    error?.message ||
    error?.reason?.message ||
    error?.payload?.message ||
    error ||
    ''
  ).toLowerCase();

  const stack = String(
    error?.stack ||
    error?.reason?.stack ||
    error?.payload?.stack ||
    ''
  ).toLowerCase();

  const filename = String(
    error?.filename ||
    error?.target?.src ||
    error?.target?.href ||
    ''
  ).toLowerCase();

  if (CHUNK_LOAD_PATTERNS.some((pattern) => message.includes(pattern))) {
    return true;
  }

  return message.includes('loading chunk') && (
    hasAssetHint(filename) ||
    hasAssetHint(stack) ||
    hasAssetHint(message)
  );
}

export function isInvalidLazyModuleError(error) {
  return error?.code === 'INVALID_LAZY_MODULE';
}

export function isStaleBuildError(error) {
  return isChunkLoadError(error) || isInvalidLazyModuleError(error);
}

export function createInvalidLazyModuleError(moduleId, resolvedModule) {
  const error = new Error(`Lazy module "${moduleId}" did not resolve to a valid default export.`);
  error.name = 'InvalidLazyModuleError';
  error.code = 'INVALID_LAZY_MODULE';
  error.moduleId = moduleId;
  error.resolvedModuleType = resolvedModule === null ? 'null' : typeof resolvedModule;
  return error;
}

export function readForcedRefreshMarker({ sessionStorageObject = getRuntimeSessionStorage() } = {}) {
  try {
    return safeReadJson(sessionStorageObject?.getItem(FORCED_REFRESH_STORAGE_KEY));
  } catch (error) {
    return null;
  }
}

export function clearForcedRefreshState({
  sessionStorageObject = getRuntimeSessionStorage(),
} = {}) {
  try {
    sessionStorageObject?.removeItem(FORCED_REFRESH_STORAGE_KEY);
  } catch (error) {
    console.warn('[staleBuildGuard] Could not clear forced refresh state:', error);
  }
}

export function hasForcedRefreshMarker({
  pathname,
  buildId,
  sessionStorageObject = getRuntimeSessionStorage(),
} = {}) {
  const marker = readForcedRefreshMarker({ sessionStorageObject });
  if (!marker) return false;

  return marker.pathname === String(pathname || '/') && marker.buildId === String(buildId || 'runtime-mismatch');
}

export function setForcedRefreshMarker({
  pathname,
  buildId,
  reason,
  sessionStorageObject = getRuntimeSessionStorage(),
} = {}) {
  try {
    const marker = {
      ...buildForcedRefreshMarker({ pathname, buildId }),
      reason: String(reason || 'unknown'),
      at: Date.now(),
    };
    sessionStorageObject?.setItem(FORCED_REFRESH_STORAGE_KEY, JSON.stringify(marker));
    return marker;
  } catch (error) {
    console.warn('[staleBuildGuard] Could not persist forced refresh marker:', error);
    return null;
  }
}

export function stripFreshBuildQueryParam({
  locationObject = getRuntimeLocation(),
  historyObject = getRuntimeHistory(),
} = {}) {
  try {
    if (!locationObject?.href || !historyObject?.replaceState) return false;

    const url = new URL(locationObject.href);
    if (!url.searchParams.has(FRESH_BUILD_QUERY_PARAM)) {
      return false;
    }

    url.searchParams.delete(FRESH_BUILD_QUERY_PARAM);
    historyObject.replaceState(historyObject.state, '', `${url.pathname}${url.search}${url.hash}`);
    return true;
  } catch (error) {
    console.warn('[staleBuildGuard] Could not strip fresh-build query param:', error);
    return false;
  }
}

export function reconcileFreshBuildQueryParam({
  locationObject = getRuntimeLocation(),
  historyObject = getRuntimeHistory(),
  sessionStorageObject = getRuntimeSessionStorage(),
} = {}) {
  try {
    if (!locationObject?.href) return false;

    const url = new URL(locationObject.href);
    const forcedBuildId = url.searchParams.get(FRESH_BUILD_QUERY_PARAM);
    if (!forcedBuildId) return false;

    const { buildId } = getLocalBuildInfo();
    if (forcedBuildId !== buildId) {
      return false;
    }

    stripFreshBuildQueryParam({ locationObject, historyObject });
    clearForcedRefreshState({ sessionStorageObject });
    return true;
  } catch (error) {
    console.warn('[staleBuildGuard] Could not reconcile fresh-build query param:', error);
    return false;
  }
}

export async function fetchRemoteBuildMeta({
  fetchImpl = getRuntimeFetch(),
  locationObject = getRuntimeLocation(),
  cacheBuster = Date.now(),
} = {}) {
  if (typeof fetchImpl !== 'function') {
    throw new Error('Build meta fetch is unavailable because fetch is not defined.');
  }

  const baseHref = locationObject?.origin || locationObject?.href || 'http://localhost';
  const url = new URL(BUILD_META_PATH, baseHref);
  url.searchParams.set('t', String(cacheBuster));

  const response = await fetchImpl(url.toString(), {
    cache: 'no-store',
    headers: {
      'Cache-Control': 'no-store',
    },
  });

  if (!response?.ok) {
    throw new Error(`Build meta request failed with status ${response?.status || 'unknown'}.`);
  }

  const payload = await response.json();
  if (!payload?.buildId) {
    throw new Error('Build meta response did not include a buildId.');
  }

  return {
    buildId: String(payload.buildId),
    builtAt: String(payload.builtAt || ''),
  };
}

export function performForcedBuildRefresh({
  remoteBuildId,
  reason,
  locationObject = getRuntimeLocation(),
  sessionStorageObject = getRuntimeSessionStorage(),
} = {}) {
  if (!locationObject?.href || typeof locationObject?.replace !== 'function') {
    return { didTriggerRefresh: false, blocked: false, reason: 'location-unavailable' };
  }

  const currentUrl = new URL(locationObject.href);
  const targetBuildId = String(remoteBuildId || 'runtime-mismatch');
  const marker = buildForcedRefreshMarker({
    pathname: currentUrl.pathname,
    buildId: targetBuildId,
  });

  if (hasForcedRefreshMarker({
    pathname: marker.pathname,
    buildId: marker.buildId,
    sessionStorageObject,
  })) {
    console.warn('[staleBuildGuard] Skipping forced refresh to avoid a loop.', {
      pathname: marker.pathname,
      buildId: marker.buildId,
      reason,
    });
    return { didTriggerRefresh: false, blocked: true, buildId: targetBuildId };
  }

  setForcedRefreshMarker({
    pathname: marker.pathname,
    buildId: marker.buildId,
    reason,
    sessionStorageObject,
  });

  currentUrl.searchParams.set(FRESH_BUILD_QUERY_PARAM, targetBuildId);

  console.warn('[staleBuildGuard] Forcing a fresh navigation for a stale build/runtime mismatch.', {
    pathname: marker.pathname,
    buildId: targetBuildId,
    reason,
  });

  locationObject.replace(currentUrl.toString());

  return {
    didTriggerRefresh: true,
    blocked: false,
    buildId: targetBuildId,
  };
}

export async function checkForUpdatedBuild({
  reason = 'resume',
  fetchImpl = getRuntimeFetch(),
  locationObject = getRuntimeLocation(),
  historyObject = getRuntimeHistory(),
  sessionStorageObject = getRuntimeSessionStorage(),
} = {}) {
  if (inFlightBuildCheck) {
    return inFlightBuildCheck;
  }

  const now = Date.now();
  if (lastBuildCheckResult && now - lastBuildCheckAt < BUILD_CHECK_TTL_MS) {
    return lastBuildCheckResult;
  }

  const task = (async () => {
    try {
      const remoteMeta = await fetchRemoteBuildMeta({ fetchImpl, locationObject });
      const { buildId: localBuildId } = getLocalBuildInfo();

      if (remoteMeta.buildId === localBuildId) {
        clearForcedRefreshState({ sessionStorageObject });
        stripFreshBuildQueryParam({ locationObject, historyObject });
        return {
          status: 'match',
          localBuildId,
          remoteBuildId: remoteMeta.buildId,
        };
      }

      const refreshResult = performForcedBuildRefresh({
        remoteBuildId: remoteMeta.buildId,
        reason,
        locationObject,
        sessionStorageObject,
      });

      return refreshResult.didTriggerRefresh
        ? {
            status: 'refreshing',
            localBuildId,
            remoteBuildId: remoteMeta.buildId,
          }
        : {
            status: 'blocked',
            localBuildId,
            remoteBuildId: remoteMeta.buildId,
          };
    } catch (error) {
      return { status: 'error', error };
    } finally {
      lastBuildCheckAt = Date.now();
      if (inFlightBuildCheck === task) {
        inFlightBuildCheck = null;
      }
    }
  })();

  inFlightBuildCheck = task;
  lastBuildCheckResult = await task;
  return lastBuildCheckResult;
}

export async function recoverFromStaleBuild({
  reason = 'runtime-mismatch',
  fetchImpl = getRuntimeFetch(),
  locationObject = getRuntimeLocation(),
  sessionStorageObject = getRuntimeSessionStorage(),
  remoteBuildId,
} = {}) {
  let targetBuildId = remoteBuildId ? String(remoteBuildId) : null;

  if (!targetBuildId) {
    try {
      const remoteMeta = await fetchRemoteBuildMeta({ fetchImpl, locationObject });
      targetBuildId = remoteMeta.buildId;
    } catch (error) {
      targetBuildId = getLocalBuildInfo().buildId || 'runtime-mismatch';
    }
  }

  return performForcedBuildRefresh({
    remoteBuildId: targetBuildId,
    reason,
    locationObject,
    sessionStorageObject,
  });
}

export function getNeverSettlingPromise() {
  return neverSettlingPromise();
}

export function resetStaleBuildGuardStateForTests() {
  inFlightBuildCheck = null;
  lastBuildCheckAt = 0;
  lastBuildCheckResult = null;
}
